import { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import { Badge } from "@/components/ui/badge";
import { Menu, Home, Map, Heart, Star, MessageCircle, CheckCircle } from "lucide-react";
import { cn } from "@/lib/utils";

const navigation = [{
  name: "Accueil",
  href: "/",
  icon: Home,
  description: "Découvrez Roca Rio"
}, {
  name: "Nos excursions",
  href: "/excursions",
  icon: Map,
  description: "11 aventures à Rio"
}, {
  name: "À propos",
  href: "/a-propos",
  icon: Heart,
  description: "Notre mission solidaire"
}];

interface HeaderProps {
  hideOnHero?: boolean;
}

export const Header = ({ hideOnHero = false }: HeaderProps) => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [scrolledPastHero, setScrolledPastHero] = useState(false);
  const location = useLocation();

  useEffect(() => {
    if (!hideOnHero) return;
    
    let ticking = false;
    const handleScroll = () => {
      if (!ticking) {
        requestAnimationFrame(() => {
          // Show header once scrolled past 80% of viewport height (hero)
          setScrolledPastHero(window.scrollY > window.innerHeight * 0.8);
          ticking = false;
        });
        ticking = true;
      }
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    handleScroll(); // Check initial state
    return () => window.removeEventListener('scroll', handleScroll);
  }, [hideOnHero]);

  const isHidden = hideOnHero && !scrolledPastHero;

  return (
    <>
      {/* Spacer to compensate for fixed header on non-hero pages */}
      {!hideOnHero && <div className="h-[72px] sm:h-[76px]" />}
      <header
        className={cn(
          "fixed top-0 left-0 right-0 z-50 transition-all duration-500",
          isHidden
            ? "-translate-y-full opacity-0 pointer-events-none"
            : "translate-y-0 opacity-100",
          "bg-background/70 backdrop-blur-xl border-b border-white/20 shadow-[0_8px_32px_0_rgba(31,38,135,0.15)]"
        )}
        role="banner"
      >
      <div className="absolute inset-0 bg-gradient-to-r from-primary/5 via-transparent to-accent/5 pointer-events-none" />
      <nav className="relative mx-auto flex max-w-7xl items-center justify-between py-2 px-4 lg:px-8 lg:py-2.5" aria-label="Navigation principale">
        <div className="flex lg:flex-1">
          <Link to="/" className="flex items-center hover:scale-105 transition-all duration-300 focus-visible-ring rounded-2xl p-1 hover:drop-shadow-2xl" aria-label="Retour à l'accueil - Roca Rio">
            <img src="/roca-rio-logo.png" alt="Roca Rio - Agence de tourisme à Rio de Janeiro" className="h-14 sm:h-16 lg:h-18 w-auto drop-shadow-lg" />
          </Link>
        </div>
        
        {/* Mobile menu */}
        <div className="flex xl:hidden">
          <Sheet open={mobileMenuOpen} onOpenChange={setMobileMenuOpen}>
            <SheetTrigger asChild>
              <Button variant="ghost" size="sm" className="-m-2.5 inline-flex items-center justify-center rounded-md p-3 min-h-[44px] min-w-[44px] focus-visible-ring" aria-label="Ouvrir le menu de navigation" aria-expanded={mobileMenuOpen} aria-controls="mobile-menu">
                <span className="sr-only">Ouvrir le menu principal</span>
                <Menu className="h-6 w-6" aria-hidden="true" />
              </Button>
            </SheetTrigger>
            <SheetContent side="right" className="w-full sm:w-96 bg-background/95 backdrop-blur-xl border-l border-white/20" id="mobile-menu">
              <div className="flex flex-col h-full">
                <div className="flex-shrink-0 mb-8">
                  <Link to="/" className="flex items-center focus-visible-ring rounded-2xl p-2" onClick={() => setMobileMenuOpen(false)} aria-label="Retour à l'accueil - Roca Rio">
                    <img src="/roca-rio-logo.png" alt="Roca Rio - Agence de tourisme à Rio de Janeiro" className="h-16 w-auto drop-shadow-lg" />
                  </Link>
                </div>
                
                <nav className="flex-1" aria-label="Navigation mobile">
                  <div className="space-y-3" role="list">
                    {navigation.map((item, index) => {
                      const Icon = item.icon;
                      const isActive = location.pathname === item.href;
                      return (
                        <Link
                          key={item.name}
                          to={item.href}
                          onClick={() => setMobileMenuOpen(false)}
                          className={cn(
                            "block rounded-xl px-4 py-3 min-h-[44px] transition-smooth group focus-visible-ring",
                            "animate-fade-in",
                            isActive
                              ? "text-primary bg-primary/10 shadow-soft"
                              : "text-foreground hover:bg-primary/5 hover:text-primary"
                          )}
                          style={{ animationDelay: `${index * 100}ms` }}
                          role="listitem"
                          aria-current={isActive ? 'page' : undefined}
                        >
                          <div className="flex items-start gap-3">
                            <Icon className="h-5 w-5 mt-0.5 transition-transform group-hover:translate-x-1" />
                            <div className="flex-1">
                              <div className="flex items-center gap-2">
                                <span className="font-semibold text-base">{item.name}</span>
                              </div>
                              <p className="text-sm text-muted-foreground mt-0.5">
                                {item.description}
                              </p>
                            </div>
                          </div>
                        </Link>
                      );
                    })}
                  </div>
                </nav>
                
                <div className="flex-shrink-0 mt-6 space-y-4 animate-fade-in" style={{ animationDelay: '400ms' }}>
                  <div className="bg-gradient-to-br from-roca-yellow/20 via-roca-orange/20 to-roca-red/10 rounded-2xl p-5 shadow-lg border border-roca-yellow/20">
                    <div className="flex justify-end mb-3">
                      <Badge className="bg-green-600/10 text-green-700 border-green-600/20">
                        👫 Impact solidaire
                      </Badge>
                    </div>
                    <h3 className="text-lg font-bold text-foreground mb-3 flex items-center gap-2">
                      <Star className="h-5 w-5 text-roca-yellow fill-roca-yellow" />
                      Prêt à explorer Rio ?
                    </h3>
                    <div className="space-y-2 mb-4">
                      <div className="flex items-center gap-2 text-sm text-foreground">
                        <CheckCircle className="h-4 w-4 text-green-600" />
                        <span>Excursions authentiques</span>
                      </div>
                      <div className="flex items-center gap-2 text-sm text-foreground">
                        <CheckCircle className="h-4 w-4 text-green-600" />
                        <span>5/5 ⭐</span>
                      </div>
                      <div className="flex items-center gap-2 text-sm text-foreground">
                        <CheckCircle className="h-4 w-4 text-green-600" />
                        <span>Guide certifié francophone</span>
                      </div>
                    </div>
                    <Button asChild className="w-full bg-gradient-to-r from-roca-yellow via-roca-orange to-roca-red text-white transition-all duration-300 hover:scale-105 min-h-[48px] shadow-md mb-3 focus-visible-ring">
                      <Link to="/contact" onClick={() => setMobileMenuOpen(false)} aria-label="Réserver mon excursion - Contact direct">
                        <MessageCircle className="w-5 h-5 mr-2" aria-hidden="true" />
                        <div className="flex flex-col items-start">
                          <span className="font-semibold">C'est parti !</span>
                          <span className="text-xs opacity-90">Réponse garantie en 24h</span>
                        </div>
                      </Link>
                    </Button>
                    <Link to="/excursions" onClick={() => setMobileMenuOpen(false)} className="block text-center text-sm text-foreground hover:text-roca-yellow transition-colors focus-visible-ring rounded-lg py-2" aria-label="Voir toutes les excursions disponibles">
                      Voir toutes les excursions →
                    </Link>
                  </div>
                  <div className="text-center text-xs text-muted-foreground pt-2 border-t border-border">
                    🇫🇷 Guide francophone • Impact solidaire
                  </div>
                </div>
              </div>
            </SheetContent>
          </Sheet>
        </div>
        
        {/* Desktop nav */}
        <div className="hidden xl:flex xl:gap-x-8 xl:ml-auto xl:mr-8" role="navigation" aria-label="Navigation principale desktop">
          {navigation.map(item => {
            const isActive = location.pathname === item.href;
            return (
              <Link
                key={item.name}
                to={item.href}
                className={cn(
                  "relative text-base xl:text-lg font-semibold leading-6 transition-all duration-300 px-4 py-3 rounded-xl focus-visible-ring backdrop-blur-sm",
                  isActive
                    ? "text-primary bg-white/80 shadow-[0_4px_16px_rgba(0,0,0,0.1)]"
                    : "text-foreground hover:text-primary hover:bg-white/50 hover:shadow-[0_4px_16px_rgba(0,0,0,0.05)]"
                )}
                aria-current={isActive ? 'page' : undefined}
                aria-label={`${item.name} - ${item.description}`}
              >
                {item.name}
              </Link>
            );
          })}
        </div>
        
        <div className="hidden xl:flex xl:flex-1 xl:justify-end xl:items-center xl:gap-3">
          <Button asChild size="sm" className="group relative shadow-lg hover:shadow-xl bg-primary hover:bg-primary/90 text-primary-foreground transition-all duration-300 min-h-[44px] px-6 py-2.5 text-sm font-semibold focus-visible-ring rounded-full">
            <Link to="/contact" aria-label="Je réserve mon excursion - Accès au formulaire de contact">
              <MessageCircle className="w-4 h-4 mr-2 transition-transform group-hover:scale-110" aria-hidden="true" />
              C'est parti !
            </Link>
          </Button>
        </div>
      </nav>
    </header>
    </>
  );
};
